'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import React from 'react';
import Logo from '@/components/atoms/logo/logo';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import AlertBanner from '@/components/molecules/alert-banner/alert-banner';
import Button from '@/components/molecules/button/button';
import { CalendarSliderTileProps } from '@/components/molecules/calendar-slider-tile/calendar-slider-tile';
import { CheckboxProps } from '@/components/molecules/checkbox/checkbox';
import DualChoice from '@/components/molecules/dual-choice/dual-choice';
import { RadiobuttonProps } from '@/components/molecules/radiobutton/radiobutton';
import { RangeSliderProps } from '@/components/molecules/range-slider/range-slider';
import Select, { SelectOption } from '@/components/molecules/select/select';
import CalendarSlider from '@/components/organisms/calendar-slider/calendar-slider';
import FilterButtonList, {
  FilterButtonItem,
} from '@/components/organisms/filter-button-list/filter-button-list';
import FilterChipList, {
  FilterChip,
} from '@/components/organisms/filter-chip-list/filter-chip-list';
import FilterSlideIn from '@/components/organisms/filter-slide-in/filter-slide-in';
import {
  DualChoiceSwitch,
  Filter,
  FilterInput,
} from '@/components/organisms/filter-slide-in/filter-slide-in-input-group/filter-slide-in-input-group';
import ProductCard, {
  ProductCardProps,
} from '@/components/organisms/product-card/product-card';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import Row from '@/components/utilities/row/row';
import { useInfiniteQueryAvailableOffers } from '@/integrations/travelportal/offers/available-offers/available-offers.hooks';
import { AdaptAvailableOffersBestOffers } from '@/integrations/travelportal/offers/available-offers/best-offers/best-offers.adapter';
import useQueryAvailableOffersBestOffers from '@/integrations/travelportal/offers/available-offers/best-offers/best-offers.hooks';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import { useSearchContext } from '@/libraries/contexts/search-context';
import {
  searchPageEvents$,
  SearchPageEventType,
} from '@/libraries/events/page-events';
import { updateUrlWithParameters } from '@/libraries/formatters/update-url-with-parameters';
import useCurrencyFormatter from '@/libraries/formatters/use-currency-formatter';
import { getLastTwoWords } from '@/libraries/getters/get-last-two-words';
import { getLogoName } from '@/libraries/getters/get-logo-name';
import { getSearchParameters } from '@/libraries/getters/get-search-parameters';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import {
  formatFilterDataForSlideIn,
  formatTravelPortalSearchResults,
  parseFilters,
} from '@/libraries/utils/search-results';
import validateSameDate from '@/libraries/validators/validate-same-date';
import styles from './travelportal-search-page-results.module.scss';

export interface HubspotFormData {
  departureDate?: string | null;
  departureName?: string | null;
  destinationName?: string | null;
  hotelName?: string | null;
  selectedPaxDistribution?: string | null;
}

const SEARCH_RESULTS_ID = 'search-results';

export interface TravelPortalSearchPageResultsProps {
  /** Search settings for filters and search bar */
  searchSettings: Content.Components['TravelPortalSearchSettingsDto'];
}

export type FilterChangeTriggerType =
  | null
  | 'userAction'
  | 'init'
  | 'priceDualChoice';

const isSameValue = <T,>(currentValue: T, nextValue: T): boolean => {
  return JSON.stringify(currentValue) === JSON.stringify(nextValue);
};

const TravelPortalSearchPageResults = ({
  searchSettings,
}: TravelPortalSearchPageResultsProps) => {
  const isLaptop = useWindowSize('laptop');
  const isTablet = useWindowSize('tablet');
  const currencyFormatter = useCurrencyFormatter();
  const { t } = useI18n();
  const [pricePerPersonDualChoice, setPricePerPersonDualChoice] =
    useState(false);
  const [selectedDepartureDate, setSelectedDepartureDate] =
    useState<Date | null>(null);
  const [showFilterSlideIn, setShowFilterSlideIn] = useState(false);
  const [sorting, setSorting] = useState<Content.Properties.SortingOptions>(
    searchSettings.selectedSortBy ?? 'websitePriority',
  );
  const [isFirstPage, setIsFirstPage] = useState(true);
  const [filterButtons, setFilterButtons] = useState<FilterButtonItem[]>([]);
  const [filterChips, setFilterChips] = useState<FilterChip[]>([]);
  const [filters, setFilters] =
    useState<TravelPortal.Properties.TravelPortalSearchFilters>([]);
  const [slideInFilters, setSlideInFilters] = useState<Filter[]>([]);
  const [scrollTargetId, setScrollTargetId] = useState<string>('');
  const [filterChangeTrigger, setFilterChangeTrigger] =
    useState<FilterChangeTriggerType>(null);
  const [formattedResults, setFormattedResults] = useState<ProductCardProps[]>(
    [],
  );
  const [alternativeDates, setAlternativeDates] = useState<
    CalendarSliderTileProps[] | undefined
  >([]);
  const router = useRouter();
  const {
    adultsNo,
    childrenAges,
    countryIds,
    departureIds,
    destinationIds,
    periodType,
    setTriggeredBy,
    triggeredBy,
  } = useSearchContext();
  const { brand } = usePageDetailsContext();
  const searchParams = useSearchParams();

  const sortingOptions: SelectOption[] = [
    { name: t('items.search.filters.popular'), value: 'websitePriority' },
    {
      name: t('items.search.filters.alphabetical'),
      value: 'hotelNameAscending',
    },
    { name: t('items.search.filters.ratingAsc'), value: 'starsAscending' },
    { name: t('items.search.filters.ratingDesc'), value: 'starsDescending' },
    { name: t('items.search.filters.priceAsc'), value: 'priceAscending' },
    { name: t('items.search.filters.priceDesc'), value: 'priceDescending' },
  ];

  const { data: dataBestOffers, isFetching: isFetchingBestOffers } =
    useQueryAvailableOffersBestOffers();

  const {
    data: dataHotelsWithOffers,
    fetchNextPage: fetchNextPageHotelsWithOffers,
    hasNextPage: hasNextPageHotelsWithOffers,
    isFetched: isFetchedHotelsWithOffers,
    isFetching: isFetchingHotelsWithOffers,
    isFetchingNextPage: isFetchingNextPageHotelsWithOffers,
  } = useInfiniteQueryAvailableOffers();

  const firstPagePagination = dataHotelsWithOffers?.pages?.[0]?.pagination as
    | Record<string, number>
    | undefined;

  const totalResults = firstPagePagination?.count || 0;
  const availableResultsCount = totalResults;
  const soldOutResultsCount = 0;
  const take = firstPagePagination?.take || 0;
  const loadedResults =
    dataHotelsWithOffers?.pages.reduce(
      (acc, page) => acc + (page?.result?.length || 0),
      0,
    ) ?? 0;
  const remainingResults = totalResults - loadedResults;
  const resultsToLoad = Math.min(take, remainingResults);
  const noResults = isFetchedHotelsWithOffers && totalResults === 0;

  useEffect(() => {
    if (isTablet) return;

    if (!isFirstPage) return;

    if (formattedResults.length === 0) return;

    const targetElement = document.getElementById(SEARCH_RESULTS_ID);

    if (!targetElement) return;

    const targetRect = targetElement.getBoundingClientRect();
    const targetTop = targetRect.top + window.scrollY;
    const currentScroll = window.scrollY;

    // If target is below the current scroll position; scroll down.
    // Prevents scrolling up when user navigates back to a scrolled search result
    if (targetTop < currentScroll) return;

    targetElement.scrollIntoView({ behavior: 'smooth' });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formattedResults]);

  const handleChangeSlider = useCallback(
    (newValue: number, filterId: string) => {
      setSlideInFilters(prevFilters => {
        const filterIndex = prevFilters.findIndex(
          filter => filter.id === filterId,
        );

        if (filterIndex === -1) return prevFilters;

        const updatedFilters = [...prevFilters];

        if (updatedFilters[filterIndex].type === FilterInput.SLIDER) {
          const updatedOptions = [
            ...updatedFilters[filterIndex].options,
          ] as RangeSliderProps[];

          updatedOptions[0] = {
            ...updatedOptions[0],
            value: newValue,
          };

          updatedFilters[filterIndex] = {
            ...updatedFilters[filterIndex],
            options: updatedOptions as RangeSliderProps[],
          };
        }

        return updatedFilters;
      });

      setFilterChangeTrigger('userAction');
    },
    [],
  );

  // change price dual choice from filer slide in
  const handleChangeDualChoice = useCallback(
    (filterId: string) => {
      setSlideInFilters(prevFilters => {
        return prevFilters.map(filter => {
          if (filter.id === filterId && filter.type === FilterInput.SLIDER) {
            const newCheckedState = !(filter.label as DualChoiceSwitch)
              .dualChoiceProps.checked;

            return {
              ...filter,
              label: {
                ...(filter.label as DualChoiceSwitch),
                dualChoiceProps: {
                  ...(filter.label as DualChoiceSwitch).dualChoiceProps,
                  checked: newCheckedState,
                },
              },
            };
          }

          return filter;
        });
      });

      setPricePerPersonDualChoice(previousValue => !previousValue);
      setFilterChangeTrigger('priceDualChoice');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pricePerPersonDualChoice],
  );

  const handleChangeCheckbox = useCallback(
    (checked: boolean, filterId: string, optionIndex: number) => {
      setSlideInFilters(prevFilters => {
        const filterIndex = prevFilters.findIndex(
          filter => filter.id === filterId,
        );

        if (filterIndex === -1) return prevFilters;

        const updatedFilters = [...prevFilters];

        if (
          updatedFilters[filterIndex].type === FilterInput.CHECKBOX ||
          updatedFilters[filterIndex].type === FilterInput.SINGLE_CHECKBOX
        ) {
          const updatedOptions = [
            ...updatedFilters[filterIndex].options,
          ] as CheckboxProps[];

          updatedOptions[optionIndex] = {
            ...updatedOptions[optionIndex],
            checked: checked,
          };

          updatedFilters[filterIndex] = {
            ...updatedFilters[filterIndex],
            options: updatedOptions as CheckboxProps[],
          };
        }

        return updatedFilters;
      });

      setFilterChangeTrigger('userAction');
    },
    [],
  );

  const handleChangeRadioButton = useCallback(
    (checked: boolean, filterId: string, optionIndex: number) => {
      setSlideInFilters(prevFilters => {
        const filterIndex = prevFilters.findIndex(
          filter => filter.id === filterId,
        );

        if (filterIndex === -1) return prevFilters;

        const updatedFilters = [...prevFilters];

        if (updatedFilters[filterIndex].type === FilterInput.RADIO) {
          const updatedOptions = updatedFilters[filterIndex].options.map(
            (option, index) => ({
              ...option,
              checked: index === optionIndex ? checked : false,
            }),
          );

          updatedFilters[filterIndex] = {
            ...updatedFilters[filterIndex],
            options: updatedOptions as RadiobuttonProps[],
          };
        }

        return updatedFilters;
      });

      setFilterChangeTrigger('userAction');
    },
    [],
  );

  // build search parameters
  const buildSearchParameters = (
    overrides: Partial<Record<string, any>> = {},
  ): Record<string, any> => {
    const urlParams = getSearchParameters(searchParams!);
    const searchParameters: Record<string, any> = {
      area: searchParams?.get('area') || undefined,
      batchSize: searchSettings.batchSize,
      countryIds,
      departureDate:
        overrides.departureDate || selectedDepartureDate?.toDateString(),
      departureIds,
      destinationIds,
      durationType: periodType,
      sortBy: overrides.sortBy || sorting,
      ...overrides,
    };

    if (adultsNo !== '' || overrides.adultsNumber) {
      searchParameters.childrenAges = overrides.childrenAges || childrenAges;
      searchParameters.adultsNumber = overrides.adultsNumber || adultsNo;
    }

    if (urlParams.durationRange && !overrides.durationRange) {
      searchParameters.durationRange = urlParams.durationRange;
    }

    return searchParameters;
  };

  // update url with given parameters
  const updateSearchUrl = ({
    filterParameters,
    searchParameters,
    shouldOverwriteUrl = false,
  }: {
    filterParameters: Record<string, any>;
    searchParameters: Record<string, any>;
    shouldOverwriteUrl?: boolean;
  }) => {
    if (
      periodType !== '' &&
      selectedDepartureDate?.toDateString() !== '' &&
      departureIds !== '' &&
      adultsNo !== ''
    ) {
      updateUrlWithParameters({
        newParameters: { ...filterParameters, ...searchParameters },
        overwrite: shouldOverwriteUrl,
        router,
      });
    }
  };

  const updateSearchUrlWithParamsAndFilters = (
    overrides: Partial<Record<string, any>> = {},
    shouldOverwriteUrl: boolean = false,
  ) => {
    const searchParameters = buildSearchParameters(overrides);
    const filterParameters = parseFilters(
      slideInFilters,
      pricePerPersonDualChoice,
      adultsNo,
    );

    updateSearchUrl({ filterParameters, searchParameters, shouldOverwriteUrl });
  };

  // set filters from searchSettings from search page
  useEffect(() => {
    setFilters(previousFilters => {
      const nextFilters = searchSettings?.filters ?? [];

      return JSON.stringify(previousFilters) === JSON.stringify(nextFilters)
        ? previousFilters
        : nextFilters;
    });

    const filterButtonsList = searchSettings?.filters.map(item => ({
      count: undefined,
      disabled: false,
      id: item.id.toString(),
      label: item.name,
      showIcon: false,
    }));

    filterButtonsList?.unshift({
      count: undefined,
      disabled: false,
      id: '0',
      label: t('items.search.filters.filter'),
      showIcon: true,
    });

    // set filter buttons from searchSettings from search page
    setFilterButtons(previousButtons => {
      const nextButtons = filterButtonsList ?? [];

      return JSON.stringify(previousButtons) === JSON.stringify(nextButtons)
        ? previousButtons
        : nextButtons;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchSettings?.filters]);

  // set filters from search results and update filters with values
  useEffect(() => {
    if (!dataHotelsWithOffers) return;

    const updateFiltersStateWithNewData = (
      initialState: TravelPortal.Properties.TravelPortalSearchFilters,
      newData: TravelPortal.Properties.TravelPortalSearchFilters = [],
    ) => {
      const merged = initialState.map(initialItem => {
        const updatedItem = newData.find(
          newItem => newItem.id === initialItem.id,
        );

        if (!updatedItem) return initialItem;

        const base = { ...initialItem, ...updatedItem };

        if (
          initialItem.$type === 'TravelPortalFacilityCheckboxFilterDto' &&
          updatedItem.$type === 'TravelPortalFacilityCheckboxFilterDto'
        ) {
          return {
            ...base,
            facilities: updatedItem.facilities ?? initialItem.facilities,
          };
        }

        if (
          initialItem.$type === 'TravelPortalFacilitySliderFilterDto' &&
          updatedItem.$type === 'TravelPortalFacilitySliderFilterDto'
        ) {
          return {
            ...base,
            facility: updatedItem.facility
              ? { ...initialItem.facility, ...updatedItem.facility }
              : initialItem.facility,
          };
        }

        return base;
      });

      // Some filters don't exist in the settings-based initial state because the
      // backend only returns them after a search has results - e.g. a distance
      // slider whose min/max come from the result set. Append those so they render.
      const additions = newData.filter(
        newItem =>
          !initialState.some(initialItem => initialItem.id === newItem.id),
      );

      return [...merged, ...additions];
    };

    // reset filters from search results if new search is made
    if (triggeredBy === 'searchButton') {
      setFilters(prevFilters =>
        (() => {
          const nextFilters = updateFiltersStateWithNewData(
            prevFilters,
            dataHotelsWithOffers?.pages[0].filters,
          );

          return isSameValue(prevFilters, nextFilters)
            ? prevFilters
            : nextFilters;
        })(),
      );

      setTriggeredBy('automaticTrigger');

      return;
    }

    if (filterChangeTrigger === 'userAction') return;

    if (!dataHotelsWithOffers?.pages[0].filters) return;

    setFilters(prevFilters =>
      (() => {
        const nextFilters = updateFiltersStateWithNewData(
          prevFilters,
          dataHotelsWithOffers?.pages[0].filters,
        );

        return isSameValue(prevFilters, nextFilters)
          ? prevFilters
          : nextFilters;
      })(),
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataHotelsWithOffers]);

  useEffect(() => {
    const subscription = searchPageEvents$.subscribe(event => {
      if (event.type !== SearchPageEventType.SEARCH) return;

      setIsFirstPage(true);
    });

    return () => subscription.unsubscribe();
  }, []);

  // format filters to slideInFilters
  useEffect(() => {
    const nextSlideInFilters = formatFilterDataForSlideIn(filters, {
      currencyFormatter,
      handleChangeCheckbox,
      handleChangeDualChoice,
      handleChangeRadioButton,
      handleChangeSlider,
      pricePerPersonDualChoice,
      t,
    });

    setSlideInFilters(previousSlideInFilters => {
      return isSameValue(previousSlideInFilters, nextSlideInFilters)
        ? previousSlideInFilters
        : nextSlideInFilters;
    });
    setFilterChangeTrigger('init');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // format filters to slideInFilters on price dual choice change
  useEffect(() => {
    const nextSlideInFilters = formatFilterDataForSlideIn(filters, {
      currencyFormatter,
      handleChangeCheckbox,
      handleChangeDualChoice,
      handleChangeRadioButton,
      handleChangeSlider,
      pricePerPersonDualChoice,
      t,
    });

    setSlideInFilters(previousSlideInFilters => {
      return isSameValue(previousSlideInFilters, nextSlideInFilters)
        ? previousSlideInFilters
        : nextSlideInFilters;
    });
    setFilterChangeTrigger('priceDualChoice');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricePerPersonDualChoice]);

  // format results to be displayed
  useEffect(() => {
    if (!dataHotelsWithOffers) return;

    const formattedSearchResults = formatTravelPortalSearchResults(
      dataHotelsWithOffers,
      {
        currencyFormatter,
        pricePerPersonDualChoice,
        t,
      },
    );

    // prevents updating formattedResults twice in case this effect runs
    // on both dataHotelsWithOffers and formatSearchResults reference changes
    // when we hit the search results page from the homepage
    setFormattedResults(previous => {
      if (JSON.stringify(previous) === JSON.stringify(formattedSearchResults)) {
        return previous;
      }

      return formattedSearchResults;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataHotelsWithOffers, pricePerPersonDualChoice]);

  // get best offers and populate calendar slider on new search
  useEffect(() => {
    const generateAlternativeDates = (
      data: AdaptAvailableOffersBestOffers,
    ): CalendarSliderTileProps[] | undefined => {
      return data.result?.map(item => {
        const date = item.departureDate
          ? new Date(item.departureDate)
          : new Date();

        return {
          active: validateSameDate(date, selectedDepartureDate as Date),
          date: date,
          weekNumber: item.weekNumber,
        };
      });
    };

    if (!dataBestOffers) return;

    if (
      triggeredBy === 'searchButton' ||
      triggeredBy === 'init' ||
      triggeredBy === 'automaticTrigger'
    ) {
      setAlternativeDates(generateAlternativeDates(dataBestOffers));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataBestOffers]);

  // set filters from search results and update filters with values
  useEffect(() => {
    const urlDefaultValues = getSearchParameters(searchParams!);

    if (!urlDefaultValues?.departureDate) return;

    const nextSelectedDepartureDate = new Date(
      urlDefaultValues.departureDate.toString(),
    );

    setSelectedDepartureDate(previousSelectedDepartureDate => {
      if (
        previousSelectedDepartureDate &&
        validateSameDate(
          previousSelectedDepartureDate,
          nextSelectedDepartureDate,
        )
      ) {
        return previousSelectedDepartureDate;
      }

      return nextSelectedDepartureDate;
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // url encode filters, execute search, update filter button count, generate filter chips
  useEffect(() => {
    const clearFilterParamsFromUrl = () => {
      const currentPath = window.location.pathname;
      const existingQueryString = window.location.search;

      if (existingQueryString !== '') {
        const existingParams = new URLSearchParams(existingQueryString);

        const filterKeys = [
          'filterPricePerPerson',
          'filterProviders',
          'filterStars',
          'filterFacilityIds',
          'filterFacilityValues',
        ];

        filterKeys.forEach(key => {
          existingParams.delete(key);
        });

        const nextQueryString = existingParams.toString();
        const newUrl = nextQueryString
          ? `${currentPath}?${nextQueryString}`
          : currentPath;
        const currentUrl = `${currentPath}${existingQueryString}`;

        if (newUrl !== currentUrl) {
          window.history.replaceState(null, '', newUrl);
        }
      }
    };

    const updateFilterButtonCounts = () => {
      setFilterButtons(prevButtons => {
        const updatedButtons = [...prevButtons];

        let totalCount = 0;

        slideInFilters.forEach(filter => {
          const filterButtonIndex = updatedButtons.findIndex(
            button => button.id === filter.id,
          );

          if (filterButtonIndex !== -1) {
            let count = 0;

            if (filter.type === FilterInput.SLIDER) {
              const sliderOption = filter.options[0];

              if (sliderOption.value !== sliderOption.maxValue) {
                count = 1;
              }
            } else if (filter.type === FilterInput.CHECKBOX) {
              count = (filter.options as CheckboxProps[]).filter(
                option => option.checked,
              ).length;
            } else if (filter.type === FilterInput.RADIO) {
              count = (filter.options as CheckboxProps[]).filter(
                option => option.checked && option.id !== null,
              ).length;
            }

            updatedButtons[filterButtonIndex] = {
              ...updatedButtons[filterButtonIndex],
              count: count === 0 ? undefined : count,
            };

            if (count > 0) {
              totalCount += count;
            }
          }
        });

        const buttonZeroIndex = updatedButtons.findIndex(
          button => button.id === '0',
        );

        if (buttonZeroIndex !== -1) {
          updatedButtons[buttonZeroIndex] = {
            ...updatedButtons[buttonZeroIndex],
            count: totalCount > 0 ? totalCount : undefined,
          };
        }

        return isSameValue(prevButtons, updatedButtons)
          ? prevButtons
          : updatedButtons;
      });
    };

    const clearFilterButtonCounts = () => {
      setFilterButtons(prevButtons => {
        const nextButtons = prevButtons.map(button => ({
          ...button,
          count: undefined,
        }));

        return isSameValue(prevButtons, nextButtons)
          ? prevButtons
          : nextButtons;
      });
    };

    const generateFiltersChips = (): FilterChip[] => {
      return slideInFilters.flatMap(filter => {
        if (
          filter.type === FilterInput.CHECKBOX ||
          filter.type === FilterInput.RADIO
        ) {
          return (filter.options as CheckboxProps[])
            .filter(option => option.checked && option.id !== null)
            .map(option => ({
              id: option.value as string,
              label: option.label ?? '',
            }));
        }

        if (filter.type === FilterInput.SLIDER) {
          const sliderOption = filter.options[0];

          if (sliderOption.value !== sliderOption.maxValue) {
            let labelContent: string;

            if (filter.name === 'TravelPortalPriceFilterDto') {
              labelContent = `${currencyFormatter(sliderOption.value as number)}`;
            } else {
              labelContent = `${sliderOption.value} ${sliderOption.unit ? sliderOption.unit : ''} ${getLastTwoWords(filter.label as string)}`;
            }

            return [
              {
                id: filter.id,
                label: `${t('items.search.filters.upTo')} ${labelContent}`,
              },
            ];
          }
        }

        return [];
      });
    };

    const clearFilterChips = () => {
      setFilterChips(previousChips => {
        return previousChips.length === 0 ? previousChips : [];
      });
    };

    const filterParameters = parseFilters(
      slideInFilters,
      pricePerPersonDualChoice,
      adultsNo,
    );
    const searchParameters = buildSearchParameters();

    if (filterChangeTrigger === 'priceDualChoice') return;

    if (Object.keys(filterParameters).length === 0) {
      clearFilterParamsFromUrl();
      clearFilterChips();
      clearFilterButtonCounts();

      return;
    } else {
      setFilterChips(previousChips => {
        const nextChips = generateFiltersChips();

        return isSameValue(previousChips, nextChips)
          ? previousChips
          : nextChips;
      });
      updateFilterButtonCounts();
    }

    if (filterChangeTrigger === 'userAction') {
      updateSearchUrl({
        filterParameters,
        searchParameters,
        shouldOverwriteUrl: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideInFilters]);

  // results page price per person dual choice
  const handleClickPriceDualChoice = () => {
    setPricePerPersonDualChoice(previousValue => !previousValue);
    setFilterChangeTrigger('priceDualChoice');
  };

  const handleChangeSorting = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setTriggeredBy('filtering');

    setSorting(event.target.value as Content.Properties.SortingOptions);
    updateSearchUrlWithParamsAndFilters({ sortBy: event.target.value });
  };

  const handleClickFilterButton = (id: string) => {
    setShowFilterSlideIn(true);
    setScrollTargetId(id);
  };

  const handleClickRemoveAllChips = () => {
    setSlideInFilters(prevFilters => {
      return prevFilters.map(filter => {
        if (filter.type === FilterInput.SLIDER) {
          const updatedOptions = filter.options.map(option => ({
            ...option,
            value: option.maxValue,
          }));

          return { ...filter, options: updatedOptions };
        } else if (
          filter.type === FilterInput.CHECKBOX ||
          filter.type === FilterInput.RADIO ||
          filter.type === FilterInput.SINGLE_CHECKBOX
        ) {
          const updatedOptions = filter.options.map(option => ({
            ...option,
            checked: false,
          }));

          return { ...filter, options: updatedOptions };
        }

        return filter;
      });
    });

    setFilterChangeTrigger('userAction');
  };

  const handleClickRemoveChip = (chipToRemove: FilterChip) => {
    setSlideInFilters(prevFilters => {
      return prevFilters.map(filter => {
        if (
          filter.type === FilterInput.SLIDER &&
          filter.id === chipToRemove.id
        ) {
          const updatedOptions = filter.options.map(option => ({
            ...option,
            value: option.maxValue,
          }));

          return { ...filter, options: updatedOptions };
        } else if (
          (filter.type === FilterInput.CHECKBOX ||
            filter.type === FilterInput.RADIO) &&
          filter.options.some(option => option.value === chipToRemove.id)
        ) {
          const updatedOptions = filter.options.map(option =>
            option.value === chipToRemove.id
              ? { ...option, checked: false }
              : option,
          );

          return { ...filter, options: updatedOptions };
        } else if (filter.type === FilterInput.SINGLE_CHECKBOX) {
          const updatedOptions = filter.options.map(option => ({
            ...option,
            checked: false,
          }));

          return { ...filter, options: updatedOptions };
        }

        return filter;
      });
    });

    setFilterChangeTrigger('userAction');
  };

  const handleClickShowMore = () => {
    setIsFirstPage(false);
    fetchNextPageHotelsWithOffers();
  };

  const handleChangeDate = (date: Date) => {
    setIsFirstPage(true);

    setTriggeredBy('calendarSlider');

    updateSearchUrlWithParamsAndFilters({
      departureDate: date.toDateString(),
    });
  };

  const renderResults = () => {
    const availableResults = formattedResults.filter(
      result => !result.isSoldOut,
    );
    const soldOutResults = formattedResults.filter(result => result.isSoldOut);

    return (
      <React.Fragment>
        {availableResults.length > 0 && (
          <React.Fragment>
            <Typography
              className={styles.heading}
              id={SEARCH_RESULTS_ID}
              tag="h2"
              tagStyle="headingSmall"
            >
              {`${availableResultsCount} ${t('items.search.results.availableResults')}`}
            </Typography>

            <div className={styles.resultsList}>
              {availableResults.map(result => (
                <ProductCard key={result.uniqueId} {...result} />
              ))}
            </div>
          </React.Fragment>
        )}

        {soldOutResults.length > 0 && (
          <div className={styles.soldOutResultsContainer}>
            <Typography
              className={styles.heading}
              tag="h2"
              tagStyle="headingSmall"
            >
              {`${soldOutResultsCount} ${t('items.search.results.soldOutResults')}`}
            </Typography>

            <div className={styles.resultsList}>
              {soldOutResults.map(result => (
                <ProductCard key={result.uniqueId} {...result} />
              ))}
            </div>
          </div>
        )}
      </React.Fragment>
    );
  };

  const renderNoResults = () => {
    return (
      <div className={styles.noResultsContainer}>
        <Logo
          className={styles.noResultsIcon}
          name={getLogoName({
            brand: brand,
            type: 'NoResults',
          })}
        />

        <div className={styles.noResultsContent}>
          <Typography
            className={styles.noResultsHeading}
            tag="h2"
            tagStyle="headingMedium"
          >
            {t('items.search.results.noResults.header')}
          </Typography>

          <Typography
            className={styles.noResultsText}
            tag="span"
            tagStyle="bodyMedium"
          >
            {t('items.search.results.noResults.text')}
          </Typography>
        </div>
      </div>
    );
  };

  const handleOnClickClear = () => {
    handleClickRemoveAllChips();
  };

  const handleOnClickViewResults = () => {
    setShowFilterSlideIn(false);
  };

  return (
    <React.Fragment>
      <section className={styles.component}>
        {((dataBestOffers?.result && dataBestOffers.result.length > 0) ||
          isFetchingBestOffers) && (
          <div className={styles.calendarWrapper}>
            <Container>
              <Row>
                <Column>
                  <CalendarSlider
                    dates={alternativeDates}
                    isLoading={isFetchingBestOffers}
                    onChange={handleChangeDate}
                    selectedDate={selectedDepartureDate as Date}
                  />
                </Column>
              </Row>
            </Container>
          </div>
        )}

        <Container>
          <Row>
            <Column>
              {triggeredBy === 'refetchAutomaticRoomDistribution' && (
                <AlertBanner
                  className={styles.alertBanner}
                  text={t(
                    'items.search.results.noResultsWithAlternatives.text',
                  )}
                />
              )}

              <div className={styles.filterAndResultsContainer}>
                {isFetchingHotelsWithOffers &&
                filterChangeTrigger !== 'userAction' ? (
                  <div className={styles.filterContainer}>
                    <div className={styles.filterButtons}>
                      <SkeletonLoader
                        borderRadius="4px"
                        height="50px"
                        width="80%"
                      />
                    </div>

                    <div className={styles.sortingContainer}>
                      <div className={styles.sorting}>
                        <SkeletonLoader
                          borderRadius="4px"
                          height="50px"
                          width="40%"
                        />

                        {isLaptop && (
                          <SkeletonLoader
                            borderRadius="4px"
                            height="50px"
                            width="40%"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  formattedResults.length > 0 && (
                    <div className={styles.filterContainer}>
                      <div className={styles.filterButtons}>
                        <FilterButtonList
                          items={isLaptop ? filterButtons : [filterButtons[0]]}
                          onClick={handleClickFilterButton}
                        />

                        {isLaptop && (
                          <FilterChipList
                            clearLabel={t(
                              'items.search.filters.clearAllFilters',
                            )}
                            items={filterChips}
                            onClickRemoveAll={handleClickRemoveAllChips}
                            onClickRemoveChip={handleClickRemoveChip}
                          />
                        )}
                      </div>

                      <div className={styles.sortingContainer}>
                        <div className={styles.sorting}>
                          <DualChoice
                            checked={pricePerPersonDualChoice}
                            labelLeft={t('items.common.words.totalPrice')}
                            labelRight={t('items.common.words.pricePerPerson')}
                            onClick={() => handleClickPriceDualChoice()}
                          />

                          {isLaptop && (
                            <Select
                              label={t('items.search.filters.sorting')}
                              onChange={handleChangeSorting}
                              options={sortingOptions}
                              value={sorting}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )
                )}

                {!isFetchingHotelsWithOffers && noResults && renderNoResults()}

                {isFetchingHotelsWithOffers &&
                !isFetchingNextPageHotelsWithOffers ? (
                  <div className={styles.resultsContainer}>
                    <SkeletonLoader
                      borderRadius="2px"
                      height="25px"
                      width="30%"
                    />

                    <div className={styles.resultsList}>
                      {Array.from({ length: 4 }).map((_, loaderIndex) => (
                        <ProductCard key={loaderIndex} isLoading />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className={styles.resultsContainer}>
                    {(availableResultsCount > 0 || soldOutResultsCount > 0) &&
                      dataHotelsWithOffers?.pages[0].pagination &&
                      renderResults()}

                    {isFetchingNextPageHotelsWithOffers && (
                      <div className={styles.resultsList}>
                        {Array.from({ length: 4 }).map((_, loaderIndex) => (
                          <ProductCard key={loaderIndex} isLoading />
                        ))}
                      </div>
                    )}

                    {hasNextPageHotelsWithOffers && (
                      <div className={styles.buttonContainer}>
                        <Button
                          disabled={
                            !hasNextPageHotelsWithOffers ||
                            isFetchingNextPageHotelsWithOffers
                          }
                          onClick={handleClickShowMore}
                          text={
                            isFetchingNextPageHotelsWithOffers
                              ? t('items.search.results.loadingResults')
                              : t('items.search.results.showMore', {
                                  number: resultsToLoad,
                                })
                          }
                          variant="secondary"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Column>
          </Row>
        </Container>
      </section>

      <FilterSlideIn
        activeFilters={{
          items: filterChips,
          onClickRemoveAll: handleClickRemoveAllChips,
          onClickRemoveChip: handleClickRemoveChip,
        }}
        filters={slideInFilters}
        headline={t('items.search.filterSlideIn.filters')}
        isOpen={showFilterSlideIn}
        onClickClear={handleOnClickClear}
        onClickViewResults={handleOnClickViewResults}
        scrollTargetId={scrollTargetId}
        setIsOpen={setShowFilterSlideIn}
      />
    </React.Fragment>
  );
};

TravelPortalSearchPageResults.displayName = 'TravelPortalSearchPageResults';

export default TravelPortalSearchPageResults;
